/**
 * Comparator for ordering building levels top-down (highest first) for display.
 *
 * Levels must be ordered by their physical `elevation`, NOT by the integer
 * `level` index. The backend can assign the same `level` value to more than one
 * building level (e.g. a "Roof" that carries `level: 3` while a regular "Level 3"
 * also exists). Sorting by `level` alone leaves those ties in insertion order,
 * which pushes the roof into the middle of the stack instead of the top.
 *
 * Primary key: elevation descending. Falls back to `level` descending when an
 * elevation is missing or when two levels share the same elevation, so callers
 * that lack elevation data still get a sensible order.
 *
 * @param {{ elevation?: number|string|null, level?: number|null }} a
 * @param {{ elevation?: number|string|null, level?: number|null }} b
 * @returns {number}
 */
export const compareLevelsDesc = (a, b) => {
	const ea = parseFloat(a?.elevation);
	const eb = parseFloat(b?.elevation);
	const aHasElevation = !Number.isNaN(ea);
	const bHasElevation = !Number.isNaN(eb);

	// Both have elevation and they differ -> order by elevation (top-down).
	if (aHasElevation && bHasElevation && ea !== eb) return eb - ea;

	// Keep levels that have an elevation above ones that don't.
	if (aHasElevation !== bHasElevation) return aHasElevation ? -1 : 1;

	// Fallback: order by the integer level index (descending).
	return (b?.level ?? 0) - (a?.level ?? 0);
};

/**
 * Returns a new array of levels sorted top-down for display.
 *
 * @template T
 * @param {T[]} levels
 * @returns {T[]}
 */
export const sortLevelsDesc = (levels) =>
	Array.isArray(levels) ? [...levels].sort(compareLevelsDesc) : levels;
